import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { IconButton, InputAdornment } from "@mui/material";
import ButtonBase from "components/shared/ButtonBase";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { SmallBody } from "helpers/TypographyComponents";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface AuthForgotPasswordProps {
    showPassword: boolean;
    error: boolean;
    errorMessage: string;
    isResetPassword: boolean;
    isLoading: boolean;
    handleEmail: (e: any) => void;
    handleNewPassword: (e: any) => void;
    handleCode: (e: any) => void;
    handleClickShowPassword: () => void;
    handleGoBack: () => void;
    handleForgottenPassword: () => void;
    handleResetPassword: () => void;
    newPassword: string;
    email: string;
}

const AuthForgotPassword = ({
    showPassword,
    error,
    errorMessage,
    isResetPassword,
    isLoading,
    handleEmail,
    handleNewPassword,
    handleCode,
    handleResetPassword,
    handleClickShowPassword,
    handleGoBack,
    handleForgottenPassword,
    newPassword,
    email,
}: AuthForgotPasswordProps) => {
    const [t] = useTranslation();

    const passwordRequirements = useMemo(
        () => ({
            MinCharacter: newPassword.length >= 8,
            HasUpperCase: /[A-Z]/.test(newPassword),
            HasLowerCase: /[a-z]/.test(newPassword),
            HasNumber: /\d/.test(newPassword),
            HasSpecialChar: /\W/.test(newPassword),
        }),
        [newPassword],
    );

    const passwordHasAllRequirements = useMemo(() => {
        return Object.values(passwordRequirements).every((value) => value);
    }, [passwordRequirements]);

    return (
        <>
            <div className="login-title trans-slide-in">
                <ArrowBackIos onClick={handleGoBack} className="login-goBack" />
                <h1>{t("LogIn.ChangePassword")}</h1>
            </div>
            <div className="login-left-container-fields trans-slide-in" key={"forgetPassword"}>
                <div className="login-left-container-field">
                    <div className="login-left-container-subtext">{t("LogIn.Email")}</div>
                    <StyledTextField placeholder="email@crealo.app" value={email} onChange={handleEmail} />
                </div>
                {isResetPassword && (
                    <>
                        <div className="login-left-container-field trans-slide-in">
                            <div className="login-left-container-subtext">{t("LogIn.Code")}</div>
                            <StyledTextField onChange={handleCode} placeholder="000000" />
                        </div>
                        <div className="login-left-container-field trans-slide-in">
                            <div className="login-left-container-subtext">{t("LogIn.NewPassword")}</div>
                            <StyledTextField
                                placeholder="•••••••••••••"
                                type={showPassword ? "text" : "password"}
                                onChange={handleNewPassword}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClickShowPassword} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {Object.entries(passwordRequirements).map(([key, value]) => (
                                <SmallBody
                                    key={key}
                                    color={value ? "success" : "grey"}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        fontWeight: value ? "500" : "400",
                                    }}
                                >
                                    <CheckCircleOutlineIcon
                                        fontSize="small"
                                        htmlColor={value ? "var(--success)" : "var(--border-color)"}
                                    />
                                    {t(`LogIn.Password${key}`)}
                                </SmallBody>
                            ))}
                        </div>
                    </>
                )}
                {!isResetPassword ? (
                    <ButtonBase
                        variant="contained"
                        styles={{ borderRadius: "4px" }}
                        disabled={!email}
                        loading={isLoading}
                        onClick={handleForgottenPassword}
                    >
                        {t("LogIn.ResetPassword")}
                    </ButtonBase>
                ) : (
                    <ButtonBase
                        variant="contained"
                        styles={{ borderRadius: "4px" }}
                        disabled={!passwordHasAllRequirements}
                        loading={isLoading}
                        onClick={handleResetPassword}
                    >
                        {t("LogIn.CreateNewPassword")}
                    </ButtonBase>
                )}
                {error && <div className="login-left-container-error">{errorMessage || t("LogIn.WentWrong")}</div>}
            </div>
        </>
    );
};

export default AuthForgotPassword;
